import { useState, useEffect, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { PageHeader } from "../components/layout/PageHeader";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  Loader2, Globe, Sparkles, ExternalLink, Copy, Check, Upload,
  Eye, ToggleLeft, UtensilsCrossed, AlertCircle, Pencil,
  Save, RotateCcw, Palette, CheckCircle2, ImageIcon, RefreshCw,
  ChevronRight, Wand2, PenLine, MousePointerClick, GripVertical,
  Lock, Crown, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useRestaurant } from "../context/RestaurantContext";

// ─── Theme definitions ────────────────────────────────────────────────────────
const THEMES = {
  mediterraneo: {
    id: "mediterraneo", name: "Mediterráneo", desc: "Cálido, familiar, colores del mar y el sol",
    preview: { bg: "#FDFCF8", primary: "#E05D3A", accent: "#7B9E6B", nav: "#2D2A26" }, emoji: "🌊",
  },
  moderno: {
    id: "moderno", name: "Moderno", desc: "Oscuro, urbano, vanguardista y dinámico",
    preview: { bg: "#0F0F0F", primary: "#FF6B35", accent: "#4ECDC4", nav: "#0F0F0F" }, emoji: "⚡",
  },
  elegante: {
    id: "elegante", name: "Elegante", desc: "Lujo, negro y dorado, experiencia premium",
    preview: { bg: "#F8F5F0", primary: "#8B6914", accent: "#1A1A1A", nav: "#1A1A1A" }, emoji: "✨",
  },
  minimalista: {
    id: "minimalista", name: "Minimalista", desc: "Limpio, blanco, foco en el producto",
    preview: { bg: "#FFFFFF", primary: "#2D2D2D", accent: "#E0E0E0", nav: "#FFFFFF" }, emoji: "◻",
  },
  rustico: {
    id: "rustico", name: "Rústico", desc: "Auténtico, artesanal, sabor tradicional",
    preview: { bg: "#F5EBD8", primary: "#8B4513", accent: "#5C8A3C", nav: "#3D2B1F" }, emoji: "🪵",
  },
  casual: {
    id: "casual", name: "Casual", desc: "Fondo oscuro, platos protagonistas, estilo asiático-fusión",
    preview: { bg: "#1A1A1A", primary: "#C8A97E", accent: "#E8DCC8", nav: "#111111" }, emoji: "🍜",
    premium: true,
  },
  casual_fast: {
    id: "casual_fast", name: "Casual Fast", desc: "Platos flotantes con movimiento, estilo fast-casual moderno",
    preview: { bg: "#1C1C1E", primary: "#FF5F2E", accent: "#FFD666", nav: "#141416" }, emoji: "🔥",
    premium: true,
  },
};

// ─── Inline editable field ────────────────────────────────────────────────────
function EditableField({ label, value, onChange, multiline = false }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);

  const commit = () => { setEditing(false); if (draft !== value) onChange(draft); };

  if (editing) {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground uppercase tracking-wider">{label}</Label>
        {multiline ? (
          <Textarea value={draft} onChange={e => setDraft(e.target.value)} onBlur={commit} autoFocus rows={4} className="text-sm resize-none" />
        ) : (
          <Input value={draft} onChange={e => setDraft(e.target.value)} onBlur={commit} onKeyDown={e => e.key === "Enter" && commit()} autoFocus className="text-sm" />
        )}
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={commit}><Check className="w-3 h-3" />Guardar</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDraft(value || ""); setEditing(false); }}>Cancelar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="group space-y-1">
      <Label className="text-xs text-muted-foreground uppercase tracking-wider">{label}</Label>
      <div className="relative cursor-text p-2 -mx-2 rounded-lg hover:bg-muted/40 transition-colors" onClick={() => setEditing(true)}>
        <p className={`text-sm leading-relaxed ${!value ? "text-muted-foreground italic" : ""}`}>{value || "Haz clic para editar..."}</p>
        <Pencil className="absolute right-2 top-2 w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </div>
  );
}

// ─── Live-edit preview: page-builder inline editing ──────────────────────────
const LIVE_THEMES = {
  mediterraneo: { bg: "#FDFCF8", primary: "#E05D3A", text: "#2D2A26", muted: "#5C5855", sectionAlt: "#F5EFE6", heroFont: "'Fraunces', serif", bodyFont: "'Manrope', sans-serif", heroOverlay: "linear-gradient(to bottom, rgba(26,23,20,0.25) 0%, rgba(26,23,20,0.78) 100%)", btn: { bg: "#E05D3A", text: "#fff" } },
  moderno:      { bg: "#0F0F0F", primary: "#FF6B35", text: "#F5F5F5", muted: "#A0A0A0", sectionAlt: "#1A1A1A", heroFont: "'DM Serif Display', serif", bodyFont: "'DM Sans', sans-serif", heroOverlay: "linear-gradient(to bottom, rgba(15,15,15,0.1) 0%, rgba(15,15,15,0.9) 100%)", btn: { bg: "#FF6B35", text: "#fff" } },
  elegante:     { bg: "#F8F5F0", primary: "#8B6914", text: "#1A1A1A", muted: "#6B6560", sectionAlt: "#F0EBE2", heroFont: "'Playfair Display', serif", bodyFont: "'Lato', sans-serif", heroOverlay: "linear-gradient(to bottom, rgba(26,26,26,0.15) 0%, rgba(26,26,26,0.85) 100%)", btn: { bg: "#8B6914", text: "#fff" } },
  minimalista:  { bg: "#FFFFFF", primary: "#2D2D2D", text: "#2D2D2D", muted: "#737373", sectionAlt: "#F7F7F7", heroFont: "'Cormorant Garamond', serif", bodyFont: "'Inter', sans-serif", heroOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.72) 100%)", btn: { bg: "#2D2D2D", text: "#fff" } },
  rustico:      { bg: "#F5EBD8", primary: "#8B4513", text: "#3D2B1F", muted: "#7A6555", sectionAlt: "#FEF8EF", heroFont: "'Crimson Text', serif", bodyFont: "'Source Serif 4', serif", heroOverlay: "linear-gradient(to bottom, rgba(61,43,31,0.15) 0%, rgba(61,43,31,0.82) 100%)", btn: { bg: "#8B4513", text: "#fff" } },
  casual:       { bg: "#1A1A1A", primary: "#C8A97E", text: "#F0EBE3", muted: "#9A9590", sectionAlt: "#222222", heroFont: "'DM Serif Display', serif", bodyFont: "'DM Sans', sans-serif", heroOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.85) 100%)", btn: { bg: "#C8A97E", text: "#1A1A1A" } },
  casual_fast:  { bg: "#1C1C1E", primary: "#FF5F2E", text: "#F5F5F7", muted: "#8E8E93", sectionAlt: "#2C2C2E", heroFont: "'DM Sans', sans-serif", bodyFont: "'DM Sans', sans-serif", heroOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.88) 100%)", btn: { bg: "#FF5F2E", text: "#fff" } },
};

const HERO_BG_FALLBACK = "https://images.unsplash.com/photo-1544148103-0773bf10d330?w=1600&q=80&auto=format&fit=crop";

// Single contentEditable field — syncs correctly with React without cursor-reset bugs
function EditInline({ field, value, as: Tag = "span", style, onSave, placeholder }) {
  const ref = useRef(null);
  const lastExternal = useRef(value);

  // Set initial content on mount
  useEffect(() => {
    if (ref.current) {
      ref.current.textContent = value || "";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only update DOM when value changed from *outside* (e.g. AI generate)
    if (ref.current && value !== lastExternal.current) {
      ref.current.textContent = value || "";
      lastExternal.current = value;
    }
  }, [value]);

  const handleBlur = e => {
    const v = e.currentTarget.textContent.trim();
    lastExternal.current = v;
    onSave(field, v);
  };

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder || ""}
      onBlur={handleBlur}
      onKeyDown={e => { if (e.key === "Enter" && Tag === "span") { e.preventDefault(); ref.current?.blur(); } }}
      onMouseEnter={e => { e.currentTarget.style.outline = "2px dashed rgba(255,255,255,0.55)"; e.currentTarget.style.outlineOffset = "4px"; e.currentTarget.style.borderRadius = "4px"; }}
      onMouseLeave={e => { e.currentTarget.style.outline = "none"; }}
      onFocus={e => { e.currentTarget.style.outline = "2px dashed rgba(255,255,255,0.85)"; e.currentTarget.style.outlineOffset = "4px"; e.currentTarget.style.borderRadius = "4px"; }}
      style={{ cursor: "text", outline: "none", display: "block", ...style }}
    />
  );
}

function EditInlineDark({ field, value, as: Tag = "span", style, onSave, placeholder, themeText }) {
  const ref = useRef(null);
  const lastExternal = useRef(value);

  // Set initial content on mount
  useEffect(() => {
    if (ref.current) {
      ref.current.textContent = value || "";
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (ref.current && value !== lastExternal.current) {
      ref.current.textContent = value || "";
      lastExternal.current = value;
    }
  }, [value]);

  const handleBlur = e => {
    const v = e.currentTarget.textContent.trim();
    lastExternal.current = v;
    onSave(field, v);
  };

  return (
    <Tag
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder || ""}
      onBlur={handleBlur}
      onKeyDown={e => { if (e.key === "Enter" && Tag === "span") { e.preventDefault(); ref.current?.blur(); } }}
      onMouseEnter={e => { e.currentTarget.style.outline = `2px dashed ${themeText || "#333"}`; e.currentTarget.style.outlineOffset = "4px"; e.currentTarget.style.borderRadius = "4px"; e.currentTarget.style.opacity = "0.85"; }}
      onMouseLeave={e => { e.currentTarget.style.outline = "none"; e.currentTarget.style.opacity = "1"; }}
      onFocus={e => { e.currentTarget.style.outline = `2px solid ${themeText || "#333"}`; e.currentTarget.style.outlineOffset = "4px"; e.currentTarget.style.borderRadius = "4px"; }}
      style={{ cursor: "text", outline: "none", display: "block", ...style }}
    />
  );
}

// ── Drag handle pill ──────────────────────────────────────────────────────────
function DragHandle({ label, isDragging }) {
  return (
    <div
      style={{
        position: "absolute", top: "10px", right: "10px", zIndex: 50,
        background: "#fff", borderRadius: "8px", padding: "4px 8px 4px 5px",
        cursor: isDragging ? "grabbing" : "grab",
        display: "flex", alignItems: "center", gap: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.06)",
        userSelect: "none", fontSize: "10px", fontWeight: 600, color: "#4B5563",
        pointerEvents: "auto",
      }}
    >
      <GripVertical size={12} color="#9CA3AF" />
      {label}
    </div>
  );
}

// ── Section labels ─────────────────────────────────────────────────────────────
const SECTION_LABELS = { hero: "Hero", about: "Sobre nosotros", specialties: "Carta" };

// Helper: determine if a hex color is "light" (for auto text contrast)
function _isLightColor(hex) {
  if (!hex || hex.length < 7) return true;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 140;
}

function LiveEditPreview({ content, selectedStyle, restaurant, logoUrl, onChangeField }) {
  const baseT = LIVE_THEMES[selectedStyle] || LIVE_THEMES.mediterraneo;
  const heroBg = content?.hero_bg_url || HERO_BG_FALLBACK;

  // Apply bg_mode override
  const t = { ...baseT };
  const bgMode = content?.bg_mode;
  if (bgMode === "light") {
    t.bg = "#FFFFFF"; t.text = "#1A1A1A"; t.muted = "#6B7280";
  } else if (bgMode === "dark") {
    t.bg = "#111111"; t.text = "#F5F5F7"; t.muted = "#9CA3AF";
  } else if (bgMode === "custom" && content?.custom_bg_color) {
    t.bg = content.custom_bg_color;
    const isLight = _isLightColor(content.custom_bg_color);
    t.text = isLight ? "#1A1A1A" : "#F5F5F7";
    t.muted = isLight ? "#6B7280" : "#9CA3AF";
  }

  // ── Drag & drop state ──
  const [sectionsOrder, setSectionsOrder] = useState(
    () => content?.sections_order || ["hero", "about", "specialties"]
  );
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dropIdx,    setDropIdx]    = useState(null);

  // Sync when AI generates new content (external change)
  useEffect(() => {
    if (content?.sections_order) setSectionsOrder(content.sections_order);
  }, [JSON.stringify(content?.sections_order)]);  // eslint-disable-line react-hooks/exhaustive-deps

  const reorder = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...sectionsOrder];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setSectionsOrder(next);
    onChangeField("sections_order", next);
  };

  const onDragStart = (e, idx) => {
    e.dataTransfer.effectAllowed = "move";
    setDraggedIdx(idx);
  };
  const onDragOver  = (e, idx) => { e.preventDefault(); setDropIdx(idx); };
  const onDrop      = (e, idx) => { e.preventDefault(); reorder(draggedIdx, idx); setDraggedIdx(null); setDropIdx(null); };
  const onDragEnd   = ()       => { setDraggedIdx(null); setDropIdx(null); };

  // ── Section renderers ──
  const renderHero = (idx) => (
    <div
      key="hero"
      draggable
      onDragStart={e => onDragStart(e, idx)}
      onDragOver={e  => onDragOver(e, idx)}
      onDrop={e      => onDrop(e, idx)}
      onDragEnd={onDragEnd}
      style={{
        position: "relative", minHeight: "340px",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden",
        opacity: draggedIdx === idx ? 0.38 : 1,
        outline: dropIdx === idx && draggedIdx !== idx ? "3px dashed #3B82F6" : "3px dashed transparent",
        outlineOffset: "-3px",
        transition: "opacity 0.18s",
      }}
    >
      <DragHandle label={SECTION_LABELS.hero} isDragging={draggedIdx === idx} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${heroBg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: selectedStyle === "moderno" ? 0.25 : 0.42 }} />
      <div style={{ position: "absolute", inset: 0, background: t.heroOverlay }} />
      <div style={{ position: "relative", zIndex: 2, textAlign: "center", padding: "40px 36px 36px", width: "100%", maxWidth: "680px" }}>
        {logoUrl && (
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <img src={logoUrl} alt="Logo" style={{ width: "72px", height: "72px", objectFit: "contain", borderRadius: "14px", boxShadow: "0 4px 20px rgba(0,0,0,0.32)", border: "2px solid rgba(255,255,255,0.18)" }} />
          </div>
        )}
        <EditInline field="hero_title"    value={content?.hero_title    || restaurant?.name || ""} style={{ fontFamily: t.heroFont, fontWeight: 300, fontStyle: "italic", fontSize: "2.6rem", lineHeight: 1.12, color: "#FDFCF8", marginBottom: "14px", textShadow: "0 2px 20px rgba(0,0,0,0.4)" }} onSave={onChangeField} placeholder="Título principal..." />
        <EditInline field="hero_subtitle" value={content?.hero_subtitle || ""}                     style={{ fontFamily: t.bodyFont, fontSize: "0.98rem", color: "rgba(255,255,255,0.82)", marginBottom: "28px", lineHeight: 1.65 }}                                                                                   onSave={onChangeField} placeholder="Subtítulo descriptivo..." />
        <div style={{ display: "inline-block", padding: "10px 32px", borderRadius: "100px", background: t.btn.bg }}>
          <EditInline field="cta_text" value={content?.cta_text || "Descubrir la carta"} style={{ fontFamily: t.bodyFont, fontWeight: 600, fontSize: "0.88rem", color: t.btn.text, letterSpacing: "0.04em" }} onSave={onChangeField} placeholder="Texto del botón..." />
        </div>
      </div>
    </div>
  );

  const renderAbout = (idx) => (
    <div
      key="about"
      draggable
      onDragStart={e => onDragStart(e, idx)}
      onDragOver={e  => onDragOver(e, idx)}
      onDrop={e      => onDrop(e, idx)}
      onDragEnd={onDragEnd}
      style={{
        position: "relative", background: t.sectionAlt, padding: "40px 36px 44px",
        opacity: draggedIdx === idx ? 0.38 : 1,
        outline: dropIdx === idx && draggedIdx !== idx ? "3px dashed #3B82F6" : "3px dashed transparent",
        outlineOffset: "-3px",
        transition: "opacity 0.18s",
      }}
    >
      <DragHandle label={SECTION_LABELS.about} isDragging={draggedIdx === idx} />
      <div style={{ maxWidth: "580px", margin: "0 auto" }}>
        <div style={{ width: "36px", height: "3px", background: t.primary, marginBottom: "20px", borderRadius: "2px" }} />
        <EditInlineDark field="about_title" value={content?.about_title || "Sobre Nosotros"} style={{ fontFamily: t.heroFont, fontStyle: "italic", fontSize: "1.75rem", color: t.text, marginBottom: "16px", lineHeight: 1.2 }} onSave={onChangeField} themeText={t.text} placeholder="Título de la sección..." />
        <EditInlineDark field="about_text" as="div" value={content?.about_text || ""} style={{ fontFamily: t.bodyFont, fontSize: "0.88rem", color: t.muted, lineHeight: 1.78, whiteSpace: "pre-wrap", minHeight: "60px" }} onSave={onChangeField} themeText={t.muted} placeholder="Escribe la historia de tu restaurante..." />
      </div>
    </div>
  );

  const renderSpecialties = (idx) => (
    <div
      key="specialties"
      draggable
      onDragStart={e => onDragStart(e, idx)}
      onDragOver={e  => onDragOver(e, idx)}
      onDrop={e      => onDrop(e, idx)}
      onDragEnd={onDragEnd}
      style={{
        position: "relative", background: t.bg, padding: "32px 36px 28px",
        opacity: draggedIdx === idx ? 0.38 : 1,
        outline: dropIdx === idx && draggedIdx !== idx ? "3px dashed #3B82F6" : "3px dashed transparent",
        outlineOffset: "-3px",
        transition: "opacity 0.18s",
      }}
    >
      <DragHandle label={SECTION_LABELS.specialties} isDragging={draggedIdx === idx} />
      <div style={{ maxWidth: "580px", margin: "0 auto" }}>
        <div style={{ width: "36px", height: "3px", background: t.primary, marginBottom: "18px", borderRadius: "2px" }} />
        <EditInlineDark field="specialties_title" value={content?.specialties_title || "Nuestra Carta"} style={{ fontFamily: t.heroFont, fontStyle: "italic", fontSize: "1.6rem", color: t.text, marginBottom: "10px", lineHeight: 1.2 }} onSave={onChangeField} themeText={t.text} placeholder="Título de la carta..." />
        <EditInlineDark field="specialties_intro" as="div" value={content?.specialties_intro || ""} style={{ fontFamily: t.bodyFont, fontSize: "0.88rem", color: t.muted, lineHeight: 1.7 }} onSave={onChangeField} themeText={t.muted} placeholder="Descripción de la carta (opcional)..." />
      </div>
    </div>
  );

  const SECTION_RENDERERS = { hero: renderHero, about: renderAbout, specialties: renderSpecialties };

  return (
    <div style={{ background: t.bg, fontFamily: t.bodyFont, height: "620px", overflowY: "auto", position: "relative" }}>
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@1,300&family=Manrope:wght@400;500;600&family=DM+Serif+Display&family=DM+Sans:wght@400;500;600&family=Playfair+Display:ital,wght@1,400&family=Lato:wght@400;500&family=Cormorant+Garamond:ital,wght@1,400&family=Inter:wght@400;500;600&family=Crimson+Text:ital,wght@1,400&family=Source+Serif+4:wght@400;500&display=swap" />

      {/* Hint bar */}
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(37,99,235,0.92)", backdropFilter: "blur(8px)", padding: "6px 14px", display: "flex", alignItems: "center", gap: "7px" }}>
        <MousePointerClick size={12} color="#fff" />
        <span style={{ color: "#fff", fontSize: "11px", fontWeight: 500 }}>
          Haz clic en cualquier texto para editar · Arrastra la pastilla blanca para reordenar secciones
        </span>
      </div>

      {/* Sections rendered in drag-defined order */}
      {sectionsOrder.map((key, idx) => SECTION_RENDERERS[key]?.(idx))}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function WebsitePage() {
  const { currentRestaurant } = useOutletContext();
  const { refreshRestaurants } = useRestaurant();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [restaurant, setRestaurant] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState("mediterraneo");
  const [content, setContent] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [editMode, setEditMode] = useState(false);

  // Logo generation state
  const [logoGenerating, setLogoGenerating] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoGallery, setLogoGallery] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);
  const heroBgInputRef = useRef(null);
  const [uploadingHeroBg, setUploadingHeroBg] = useState(false);
  const [generatingHeroBg, setGeneratingHeroBg] = useState(false);
  const [restaurantBgPhotos, setRestaurantBgPhotos] = useState([]);

  // Plan features state
  const [planFeatures, setPlanFeatures] = useState(null);
  const [planName, setPlanName] = useState("");

  useEffect(() => {
    if (currentRestaurant?.logo_url) setLogoUrl(currentRestaurant.logo_url);
  }, [currentRestaurant]);

  useEffect(() => {
    if (!currentRestaurant) return;
    setRestaurant(currentRestaurant);
    const wc = currentRestaurant.website_content;
    if (wc) { setContent(wc); setSelectedStyle(wc.style || "mediterraneo"); }
    setLoading(false);
    // Load logo gallery + bg photos + plan features
    api.getLogoGallery(currentRestaurant.restaurant_id)
      .then(res => setLogoGallery(res.logos || []))
      .catch(() => setLogoGallery([]));
    api.getBackgroundPhotos(currentRestaurant.restaurant_id)
      .then(res => setRestaurantBgPhotos(res.photos || []))
      .catch(() => setRestaurantBgPhotos([]));
    api.request(`/restaurants/${currentRestaurant.restaurant_id}/website/features`)
      .then(res => { setPlanFeatures(res.features || {}); setPlanName(res.plan_name || ""); })
      .catch(() => setPlanFeatures({}));
  }, [currentRestaurant]);

  const webSlug = restaurant?.web_slug || "";
  const publicUrl = webSlug ? `${window.location.origin}/r/${webSlug}` : null;

  const toggleEditMode = async () => {
    if (editMode && isDirty) {
      // Auto-save when leaving edit mode
      await handleSaveContent();
    }
    setEditMode(v => !v);
  };

  const updateField = (key, value) => { setContent(prev => ({ ...prev, [key]: value })); setIsDirty(true); };

  const handleGenerateContent = async () => {
    setGenerating(true); setIsDirty(false);
    try {
      const generated = await api.aiGenerateWebsiteContent(currentRestaurant.restaurant_id, selectedStyle);
      setContent(generated);
      setRestaurant(r => ({ ...r, website_content: generated }));
      await refreshRestaurants();
      setIframeKey(k => k + 1);
      toast.success("Contenido generado y guardado");
    } catch { toast.error("Error al generar contenido"); }
    finally { setGenerating(false); }
  };

  const handleSaveContent = async () => {
    if (!content) return;
    setSaving(true);
    try {
      const saved = await api.saveWebsiteContent(currentRestaurant.restaurant_id, content);
      setContent(saved);
      setRestaurant(r => ({ ...r, website_content: saved }));
      await refreshRestaurants();
      setIsDirty(false);
      setIframeKey(k => k + 1);
      toast.success("Cambios guardados");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleToggleWebsite = async (enabled) => {
    setToggling(true);
    try {
      await api.toggleWebsite(currentRestaurant.restaurant_id, enabled);
      setRestaurant(r => ({ ...r, website_enabled: enabled }));
      await refreshRestaurants();
      toast.success(enabled ? "Web pública activada" : "Web desactivada");
    } catch { toast.error("Error al cambiar estado"); }
    finally { setToggling(false); }
  };

  // ─── Logo generation ──────────────────────────────────────────────────────
  const handleGenerateLogo = async () => {
    setLogoGenerating(true);
    try {
      const result = await api.generateRestaurantLogo(currentRestaurant.restaurant_id);
      if (result.success) {
        const newUrl = result.logo_url;
        // Save to gallery (prepend, max 10)
        const newGallery = [newUrl, ...logoGallery.filter(u => u !== newUrl)].slice(0, 10);
        await api.updateLogoGallery(currentRestaurant.restaurant_id, newGallery, newUrl);
        setLogoGallery(newGallery);
        setLogoUrl(newUrl);
        setRestaurant(r => ({ ...r, logo_url: newUrl, logo_gallery: newGallery }));
        await refreshRestaurants();
        toast.success("Logo generado y guardado en galería");
      } else {
        toast.error("No se pudo generar el logo");
      }
    } catch (err) {
      toast.error(err.message || "Error al generar logo");
    } finally {
      setLogoGenerating(false);
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Máximo 5MB"); return; }
    setUploadingLogo(true);
    try {
      const up = await api.uploadImage(file);
      if (up.success) {
        const newUrl = up.image_url;
        const newGallery = [newUrl, ...logoGallery.filter(u => u !== newUrl)].slice(0, 10);
        await api.updateLogoGallery(currentRestaurant.restaurant_id, newGallery, newUrl);
        setLogoGallery(newGallery);
        setLogoUrl(newUrl);
        setRestaurant(r => ({ ...r, logo_url: newUrl, logo_gallery: newGallery }));
        await refreshRestaurants();
        toast.success("Logo subido y guardado en galería");
      }
    } catch { toast.error("Error al subir logo"); }
    finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const handleSetActiveLogo = async (url) => {
    try {
      await api.updateLogoGallery(currentRestaurant.restaurant_id, logoGallery, url);
      setLogoUrl(url);
      setRestaurant(r => ({ ...r, logo_url: url }));
      await refreshRestaurants();
      toast.success("Logo activado");
    } catch { toast.error("Error al activar logo"); }
  };

  const handleRemoveLogoFromGallery = async (url) => {
    const newGallery = logoGallery.filter(u => u !== url);
    const newActive = logoUrl === url ? (newGallery[0] || null) : logoUrl;
    await api.updateLogoGallery(currentRestaurant.restaurant_id, newGallery, newActive);
    setLogoGallery(newGallery);
    if (newActive !== logoUrl) {
      setLogoUrl(newActive);
      setRestaurant(r => ({ ...r, logo_url: newActive, logo_gallery: newGallery }));
      await refreshRestaurants();
    }
    toast.success("Logo eliminado de la galería");
  };

  // ─── Hero background ─────────────────────────────────────────────────────
  const handleUploadHeroBg = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Máximo 5MB"); return; }
    setUploadingHeroBg(true);
    try {
      const up = await api.uploadImage(file);
      if (up.success) {
        const newUrl = up.image_url;
        // Save to bg_photos gallery too
        const newPhotos = [...restaurantBgPhotos, newUrl];
        await api.updateBackgroundPhotos(currentRestaurant.restaurant_id, newPhotos);
        setRestaurantBgPhotos(newPhotos);
        // Set as hero background
        updateField("hero_bg_url", newUrl);
        toast.success("Imagen de fondo guardada");
      }
    } catch { toast.error("Error al subir imagen de fondo"); }
    finally {
      setUploadingHeroBg(false);
      if (heroBgInputRef.current) heroBgInputRef.current.value = "";
    }
  };

  const handleGenerateHeroBg = async () => {
    setGeneratingHeroBg(true);
    try {
      const result = await api.generateHeroImage(currentRestaurant.restaurant_id);
      if (result.success && result.image_url) {
        setContent(prev => ({ ...prev, hero_bg_url: result.image_url }));
        toast.success("Imagen de hero generada con IA");
      } else {
        toast.error(result.error || "No se pudo generar la imagen");
      }
    } catch (e) {
      if (e?.status === 402) {
        toast.error("Sin creditos suficientes para generar imagen");
      } else {
        toast.error(e?.message || "Error al generar imagen de hero");
      }
    }
    setGeneratingHeroBg(false);
  };

  const copyWebsiteUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success("URL copiada");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const activeTheme = THEMES[selectedStyle];
  const ft = planFeatures || {};
  const themesAllowed = ft.themes_allowed || Object.keys(THEMES);

  // Locked overlay for gated features
  const LockedOverlay = ({ feature, label }) => (
    <div
      className="absolute inset-0 z-10 bg-background/60 backdrop-blur-[2px] rounded-xl flex flex-col items-center justify-center gap-2 cursor-pointer"
      data-testid={`locked-${feature}`}
      onClick={() => navigate("/app/subscription")}
    >
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20">
        <Lock className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-xs font-semibold text-amber-700">{label || "Mejora tu plan"}</span>
      </div>
      <span className="text-[11px] text-primary flex items-center gap-1">
        <Crown className="w-3 h-3" /> Ver planes <ArrowUpRight className="w-3 h-3" />
      </span>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="website-page">
      {/* Header */}
      <PageHeader
        icon={<Globe className="w-5 h-5" />}
        title="Página Web"
        description="Diseña y publica la web de tu restaurante."
        breadcrumb={[{ label: "Presencia digital" }, { label: "Web" }]}
        actions={
          isDirty && (
            <Button onClick={handleSaveContent} disabled={saving} className="gap-2" data-testid="save-content-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar cambios
            </Button>
          )
        }
      />

      <div className="grid xl:grid-cols-2 gap-6">
        {/* ── LEFT COLUMN: Controls ──────────────────────────────────────── */}
        <div className="space-y-5">

          {/* Step 1: Style picker */}
          <Card className="border-none shadow-soft" data-testid="style-picker">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <Palette className="w-4 h-4 text-primary" />
                1. Elige el estilo visual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-2">
                {Object.values(THEMES).map(theme => {
                  const isSelected = selectedStyle === theme.id;
                  const isLocked = !themesAllowed.includes(theme.id);
                  const lockLabel = theme.premium ? "Premium" : "Pro";
                  return (
                    <button key={theme.id} onClick={() => {
                      if (isLocked) { toast.error(`Tema "${theme.name}" disponible desde el plan ${lockLabel}`); return; }
                      setSelectedStyle(theme.id);
                      if (content) updateField("style", theme.id);
                    }}
                      data-testid={`style-${theme.id}`}
                      className={`relative flex flex-col gap-1.5 p-2 rounded-xl border-2 text-left transition-all ${isLocked ? "opacity-50 border-border cursor-not-allowed" : isSelected ? "border-primary shadow-md scale-[1.02]" : "border-border hover:border-primary/40"}`}>
                      <div className="h-12 rounded-lg overflow-hidden flex flex-col" style={{ background: theme.preview.bg }}>
                        <div className="h-4 flex items-center px-1.5 gap-0.5" style={{ background: theme.preview.nav }}>
                          <div className="w-2 h-0.5 rounded-full opacity-70" style={{ background: theme.preview.primary }} />
                          <div className="w-3 h-0.5 rounded-full opacity-40" style={{ background: theme.preview.primary }} />
                        </div>
                        <div className="flex-1 flex items-center justify-center px-1">
                          <div className="w-full h-1.5 rounded-full" style={{ background: theme.preview.primary, opacity: 0.8 }} />
                        </div>
                        <div className="flex gap-0.5 px-1 pb-1">
                          {[0, 1, 2].map(i => <div key={i} className="flex-1 h-1 rounded" style={{ background: theme.preview.accent, opacity: 0.5 }} />)}
                        </div>
                      </div>
                      <p className="font-medium text-xs text-center truncate">{theme.name}</p>
                      {isSelected && !isLocked && <CheckCircle2 className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-primary" />}
                      {isLocked && <Lock className="absolute top-1.5 right-1.5 w-3.5 h-3.5 text-amber-500" />}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Step 1.5: Background customization */}
          <Card className="border-none shadow-soft" data-testid="bg-customizer">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <Palette className="w-4 h-4 text-primary" />
                1.5 Color de fondo
              </CardTitle>
              <CardDescription className="text-xs">Personaliza el fondo de la web del restaurante</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                {[
                  { id: "default", label: "Por defecto", color: null },
                  { id: "light", label: "Claro", color: "#FFFFFF" },
                  { id: "dark", label: "Oscuro", color: "#111111" },
                  { id: "custom", label: "Personalizado", color: content?.custom_bg_color || "#2C2C2E" },
                ].map(opt => {
                  const currentMode = content?.bg_mode || "default";
                  const isActive = currentMode === opt.id;
                  return (
                    <button
                      key={opt.id}
                      onClick={() => {
                        updateField("bg_mode", opt.id);
                        if (opt.id !== "custom" && opt.color) updateField("custom_bg_color", opt.color);
                      }}
                      className={`flex-1 flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all text-xs font-medium ${isActive ? "border-primary" : "border-border hover:border-primary/40"}`}
                      data-testid={`bg-mode-${opt.id}`}
                    >
                      <div
                        className="w-8 h-8 rounded-md border"
                        style={{
                          background: opt.id === "default"
                            ? (LIVE_THEMES[selectedStyle]?.bg || "#fff")
                            : opt.color,
                          borderColor: opt.id === "default" ? "var(--border)" : (opt.color === "#FFFFFF" ? "#ddd" : "transparent"),
                        }}
                      />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {(content?.bg_mode === "custom") && (
                <div className="flex items-center gap-3">
                  <label className="text-xs text-muted-foreground shrink-0">Color:</label>
                  <input
                    type="color"
                    value={content?.custom_bg_color || "#2C2C2E"}
                    onChange={e => updateField("custom_bg_color", e.target.value)}
                    className="w-10 h-8 rounded cursor-pointer border-0 p-0"
                    data-testid="custom-bg-color-input"
                  />
                  <Input
                    value={content?.custom_bg_color || "#2C2C2E"}
                    onChange={e => updateField("custom_bg_color", e.target.value)}
                    className="w-24 h-8 text-xs font-mono"
                    placeholder="#2C2C2E"
                    data-testid="custom-bg-color-text"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Logo */}
          <Card className="border-none shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <ImageIcon className="w-4 h-4 text-primary" />
                2. Logo del restaurante
              </CardTitle>
              <CardDescription className="text-xs">Sube tu logo o genera uno con IA — se guarda en galería (máx. 10)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Active logo preview + actions */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-2xl border-2 border-dashed border-border flex items-center justify-center bg-muted/20 shrink-0 overflow-hidden">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-full h-full object-cover rounded-2xl" data-testid="restaurant-logo-preview" />
                  ) : (
                    <span className="text-3xl">🍽️</span>
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <Button onClick={handleGenerateLogo} disabled={logoGenerating} className="w-full rounded-full gap-2" data-testid="generate-logo-btn">
                    {logoGenerating
                      ? <><Loader2 className="w-4 h-4 animate-spin" />Generando logo...</>
                      : <><Sparkles className="w-4 h-4" />{logoUrl ? "Regenerar con IA" : "Generar logo con IA"}</>
                    }
                  </Button>
                  <Button variant="outline" onClick={() => logoInputRef.current?.click()} disabled={uploadingLogo} className="w-full rounded-full gap-2 text-xs h-8" data-testid="upload-logo-btn">
                    {uploadingLogo ? <><Loader2 className="w-3 h-3 animate-spin" />Subiendo...</> : <><Upload className="w-3 h-3" />Subir mi logo</>}
                  </Button>
                  <input ref={logoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadLogo} />
                </div>
              </div>

              {/* Logo gallery */}
              {logoGallery.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Galería ({logoGallery.length}/10) — clic para activar</p>
                  <div className="flex gap-2 flex-wrap">
                    {logoGallery.map((url, idx) => (
                      <div key={`logo-${url.slice(-12)}`} className="relative group">
                        <button
                          onClick={() => handleSetActiveLogo(url)}
                          data-testid={`logo-gallery-item-${idx}`}
                          className={`w-14 h-14 rounded-xl overflow-hidden border-2 transition-all ${
                            logoUrl === url ? "border-primary shadow-md scale-105" : "border-transparent hover:border-primary/60"
                          }`}
                        >
                          <img src={url} alt={`Logo ${idx + 1}`} className="w-full h-full object-cover" />
                          {logoUrl === url && (
                            <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                              <Check className="w-4 h-4 text-primary" />
                            </div>
                          )}
                        </button>
                        <button
                          onClick={() => handleRemoveLogoFromGallery(url)}
                          className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-destructive text-destructive-foreground rounded-full hidden group-hover:flex items-center justify-center text-[10px]"
                        >×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 2.5: Hero background image */}
          <Card className="border-none shadow-soft relative">
            {!ft.custom_hero && <LockedOverlay feature="custom_hero" label="Imagen hero desde Starter" />}
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <ImageIcon className="w-4 h-4 text-primary" />
                2.5 Imagen de fondo del hero
              </CardTitle>
              <CardDescription className="text-xs">Selecciona o sube una foto que se fusionará como fondo en tu web</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Current hero bg preview */}
              {content?.hero_bg_url && (
                <div className="relative rounded-lg overflow-hidden h-24">
                  <img src={content.hero_bg_url} alt="Fondo actual" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <span className="text-white text-xs font-medium">Fondo activo</span>
                  </div>
                  <button
                    onClick={() => updateField("hero_bg_url", null)}
                    className="absolute top-2 right-2 w-6 h-6 bg-black/60 text-white rounded-full flex items-center justify-center text-xs hover:bg-black/80"
                    title="Eliminar fondo personalizado"
                  >×</button>
                </div>
              )}

              {/* Gallery of restaurant bg photos to select */}
              {restaurantBgPhotos.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Selecciona de tu galería</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {restaurantBgPhotos.map((url, idx) => (
                      <button
                        key={`herobg-${url.slice(-12)}`}
                        onClick={() => updateField("hero_bg_url", url)}
                        data-testid={`hero-bg-option-${idx}`}
                        className={`shrink-0 relative rounded-lg overflow-hidden border-2 transition-all ${
                          content?.hero_bg_url === url ? "border-primary scale-105" : "border-transparent hover:border-primary/60"
                        }`}
                      >
                        <img src={url} alt={`Fondo ${idx + 1}`} className="w-20 h-14 object-cover" />
                        {content?.hero_bg_url === url && (
                          <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Generate hero bg with AI */}
              <Button
                onClick={handleGenerateHeroBg}
                disabled={generatingHeroBg || uploadingHeroBg}
                className="w-full gap-2 text-xs h-8 rounded-full"
                data-testid="generate-hero-bg-btn"
              >
                {generatingHeroBg
                  ? <><Loader2 className="w-3 h-3 animate-spin" />Generando con IA...</>
                  : <><Sparkles className="w-3 h-3" />Generar imagen de hero con IA</>
                }
              </Button>

              {/* Upload new bg image */}
              <Button variant="outline" onClick={() => heroBgInputRef.current?.click()} disabled={uploadingHeroBg || generatingHeroBg} className="w-full gap-2 text-xs h-8 rounded-full" data-testid="upload-hero-bg-btn">
                {uploadingHeroBg ? <><Loader2 className="w-3 h-3 animate-spin" />Subiendo...</> : <><Upload className="w-3 h-3" />Subir imagen de fondo</>}
              </Button>
              <input ref={heroBgInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUploadHeroBg} />
              {!content?.hero_bg_url && (
                <p className="text-xs text-muted-foreground text-center">Sin imagen seleccionada · se usa el fondo por defecto del tema</p>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Generate content */}
          <Card className="border-none shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <Wand2 className="w-4 h-4 text-primary" />
                3. Genera contenido con IA
              </CardTitle>
              <CardDescription className="text-xs">Textos adaptados al estilo <strong>{activeTheme?.name}</strong> y tu menú activo</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleGenerateContent} disabled={generating} className="w-full rounded-full" data-testid="generate-content-btn">
                {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando...</> : <><Sparkles className="w-4 h-4 mr-2" />{content ? "Regenerar contenido" : "Generar con IA"}</>}
              </Button>
            </CardContent>
          </Card>

          {/* Step 4: Edit content */}
          {content && (
            <Card className="border-none shadow-soft">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2 text-base">
                    <Pencil className="w-4 h-4 text-primary" />
                    4. Edita el contenido
                  </CardTitle>
                  {isDirty && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" className="gap-1 text-xs h-7" onClick={() => { setContent(restaurant?.website_content); setIsDirty(false); }}>
                        <RotateCcw className="w-3 h-3" />Descartar
                      </Button>
                      <Button size="sm" className="gap-1 text-xs h-7" onClick={handleSaveContent} disabled={saving} data-testid="save-inline-btn">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}Guardar
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription className="text-xs">Haz clic en cualquier texto para editarlo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                <EditableField label="Titulo principal (Hero)" value={content.hero_title} onChange={v => updateField("hero_title", v)} />
                <EditableField label="Subtitulo" value={content.hero_subtitle} onChange={v => updateField("hero_subtitle", v)} />
                {ft.show_about !== false ? (
                  <>
                    <EditableField label="Titulo 'Sobre Nosotros'" value={content.about_title} onChange={v => updateField("about_title", v)} />
                    <EditableField label="Historia del restaurante" value={content.about_text} onChange={v => updateField("about_text", v)} multiline />
                  </>
                ) : (
                  <div onClick={() => navigate("/app/subscription")} className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs text-amber-700 cursor-pointer hover:bg-amber-500/10 transition-colors">
                    <Lock className="w-3.5 h-3.5 flex-shrink-0" /> Seccion "Sobre Nosotros" disponible desde Starter <ArrowUpRight className="w-3 h-3 ml-auto" />
                  </div>
                )}
                <EditableField label="Titulo especialidades" value={content.specialties_title} onChange={v => updateField("specialties_title", v)} />
                <EditableField label="Intro especialidades" value={content.specialties_intro} onChange={v => updateField("specialties_intro", v)} multiline />
                <EditableField label="Llamada a la accion (CTA)" value={content.cta_text} onChange={v => updateField("cta_text", v)} />
                {ft.custom_footer !== false ? (
                  <EditableField label="Frase del footer" value={content.footer_tagline} onChange={v => updateField("footer_tagline", v)} />
                ) : (
                  <div onClick={() => navigate("/app/subscription")} className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs text-amber-700 cursor-pointer hover:bg-amber-500/10 transition-colors">
                    <Lock className="w-3.5 h-3.5 flex-shrink-0" /> Footer personalizable desde Starter <ArrowUpRight className="w-3 h-3 ml-auto" />
                  </div>
                )}
                {ft.seo_tags ? (
                  <EditableField label="Descripcion SEO" value={content.seo_description} onChange={v => updateField("seo_description", v)} multiline />
                ) : (
                  <div onClick={() => navigate("/app/subscription")} className="flex items-center gap-2 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/15 text-xs text-amber-700 cursor-pointer hover:bg-amber-500/10 transition-colors">
                    <Lock className="w-3.5 h-3.5 flex-shrink-0" /> SEO disponible desde Pro <ArrowUpRight className="w-3 h-3 ml-auto" />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Step 5: Publish */}
          <Card className="border-none shadow-soft">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <ToggleLeft className="w-4 h-4 text-primary" />
                5. Publica tu web
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label>Web pública</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{restaurant?.website_enabled ? "Visible para todos" : "Desactivada"}</p>
                </div>
                <Switch checked={restaurant?.website_enabled || false} onCheckedChange={handleToggleWebsite} disabled={toggling || !content} data-testid="website-toggle" />
              </div>
              {!content && (
                <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Genera el contenido con IA para activar la web</span>
                </div>
              )}
              {publicUrl && (
                <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                  <Label className="text-xs text-muted-foreground">URL de tu web</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-background px-2 py-1.5 rounded border truncate font-mono">{publicUrl}</code>
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={copyWebsiteUrl} data-testid="copy-url-btn">
                      {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8" asChild data-testid="open-url-btn">
                      <a href={publicUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ── RIGHT COLUMN: Live iframe preview ─────────────────────────── */}
        <div className="xl:sticky xl:top-6 xl:self-start" data-testid="preview-panel">
          <Card className="border-none shadow-soft overflow-hidden">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Eye className="w-4 h-4 text-primary" />
                  Vista previa en tiempo real
                </CardTitle>
                <div className="flex items-center gap-2">
                  {!editMode && (
                    <Button size="sm" variant="ghost" className="h-7 gap-1.5 text-xs text-muted-foreground"
                      onClick={() => setIframeKey(k => k + 1)} data-testid="refresh-preview-btn">
                      <RefreshCw className="w-3 h-3" />Actualizar
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant={editMode ? "default" : "outline"}
                    className={`h-7 gap-1.5 text-xs ${editMode ? "bg-blue-600 hover:bg-blue-700 text-white" : ""}`}
                    onClick={toggleEditMode}
                    data-testid="toggle-edit-mode-btn"
                  >
                    <PenLine className="w-3 h-3" />
                    {editMode ? (saving ? "Guardando..." : "Salir y guardar") : "Editar en vivo"}
                  </Button>
                  {restaurant?.website_enabled && publicUrl && (
                    <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" asChild>
                      <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="preview-link">
                        <ExternalLink className="w-3 h-3" />Abrir
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0">
              {/* ── Live Edit Mode ── */}
              {editMode ? (
                <div className="rounded-xl overflow-hidden border border-blue-300 dark:border-blue-700 shadow-inner" data-testid="live-edit-preview">
                  {/* Fake browser chrome */}
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-blue-50 dark:bg-blue-950/30 border-b border-blue-200 dark:border-blue-800">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    <div className="flex-1 mx-2 bg-blue-100 dark:bg-blue-900/40 rounded text-[10px] text-blue-600 dark:text-blue-300 px-2 py-0.5 truncate font-mono">
                      {publicUrl || "menugenius.online/r/..."}
                    </div>
                    <span className="text-[10px] text-blue-500 font-medium flex items-center gap-1">
                      <PenLine className="w-2.5 h-2.5" />EDIT
                    </span>
                  </div>
                  <LiveEditPreview
                    content={content}
                    selectedStyle={selectedStyle}
                    restaurant={restaurant}
                    logoUrl={logoUrl}
                    onChangeField={updateField}
                  />
                </div>
              ) : publicUrl && restaurant?.website_enabled ? (
                /* Live iframe */
                <div className="rounded-xl overflow-hidden border border-border shadow-inner bg-muted/20">
                  {/* Fake browser chrome */}
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-muted/60 border-b">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    <div className="flex-1 mx-2 bg-background rounded text-[10px] text-muted-foreground px-2 py-0.5 truncate font-mono">
                      {publicUrl}
                    </div>
                  </div>
                  <div style={{ height: "620px", position: "relative" }}>
                    <iframe
                      ref={iframeRef}
                      key={iframeKey}
                      src={publicUrl}
                      title="Vista previa"
                      style={{
                        width: "200%", height: "200%",
                        transform: "scale(0.5)", transformOrigin: "top left",
                        border: "none", pointerEvents: "none",
                      }}
                      data-testid="preview-iframe"
                    />
                  </div>
                </div>
              ) : (
                /* Static mockup when not published */
                <div className="rounded-xl overflow-hidden border border-border">
                  <div className="flex items-center gap-1.5 px-3 py-2 bg-muted/60 border-b">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                    <div className="flex-1 mx-2 bg-background rounded text-[10px] text-muted-foreground/50 px-2 py-0.5 font-mono">
                      {publicUrl || "menugenius.online/r/..."}
                    </div>
                  </div>
                  <div style={{ background: activeTheme?.preview.bg }}>
                    <div className="p-4 text-center" style={{ background: activeTheme?.preview.nav, minHeight: "70px" }}>
                      <p className="font-bold text-sm truncate" style={{ color: activeTheme?.preview.bg }}>{content?.hero_title || restaurant?.name}</p>
                      <p className="text-xs mt-0.5 opacity-60 truncate" style={{ color: activeTheme?.preview.bg }}>{content?.hero_subtitle || "—"}</p>
                    </div>
                    <div className="p-4 space-y-3">
                      <div>
                        <p className="text-xs font-bold uppercase" style={{ color: activeTheme?.preview.primary }}>{content?.about_title || "Sobre Nosotros"}</p>
                        <p className="text-xs mt-0.5 opacity-60 line-clamp-2">{content?.about_text || "Tu historia..."}</p>
                      </div>
                      <div className="pt-1">
                        <div className="text-center py-1.5 rounded-full text-xs font-semibold" style={{ background: activeTheme?.preview.primary, color: "#fff" }}>
                          {content?.cta_text || "Reserva ahora"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Publish CTA */}
                  {content && !restaurant?.website_enabled && (
                    <div className="p-3 border-t bg-muted/30 text-center">
                      <p className="text-xs text-muted-foreground mb-2">Activa la web para ver la previsualización en tiempo real</p>
                      <Button size="sm" onClick={() => handleToggleWebsite(true)} disabled={toggling} className="rounded-full gap-1.5 text-xs">
                        {toggling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Globe className="w-3 h-3" />}
                        Activar web
                      </Button>
                    </div>
                  )}
                  {!content && (
                    <div className="p-4 border-t bg-muted/20 text-center">
                      <UtensilsCrossed className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" />
                      <p className="text-xs text-muted-foreground">Genera el contenido para ver la previsualización</p>
                    </div>
                  )}
                </div>
              )}

              {/* Sections checklist */}
              {content && (
                <div className="mt-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Secciones</p>
                    {planName && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                        <Crown className="w-2.5 h-2.5" /> {planName}
                      </span>
                    )}
                  </div>
                  {[
                    { name: "Hero", done: !!content.hero_title, locked: false },
                    { name: "Sobre nosotros", done: !!content.about_text, locked: ft.show_about === false },
                    { name: "Menu activo", done: true, locked: false },
                    { name: "Especialidades", done: !!content.specialties_intro, locked: false },
                    { name: "Contacto", done: !!restaurant?.address, locked: ft.show_contact === false },
                    { name: "Horarios", done: !!content.schedule, locked: ft.show_schedule === false },
                    { name: "Fidelizacion", done: true, locked: ft.show_loyalty === false },
                    { name: "WhatsApp", done: !!restaurant?.phone, locked: ft.show_whatsapp === false },
                    { name: "SEO meta tags", done: !!content.seo_description, locked: !ft.seo_tags },
                    { name: "Sin branding", done: ft.hide_branding, locked: !ft.hide_branding },
                    { name: "Dominio propio", done: !!restaurant?.custom_domain, locked: !ft.custom_domain },
                  ].map((s) => (
                    <div key={s.name} className="flex items-center gap-2 text-xs">
                      {s.locked ? (
                        <Lock className="w-3.5 h-3.5 flex-shrink-0 text-amber-500/60" />
                      ) : (
                        <CheckCircle2 className={`w-3.5 h-3.5 flex-shrink-0 ${s.done ? "text-green-500" : "text-muted-foreground/30"}`} />
                      )}
                      <span className={s.locked ? "text-muted-foreground/50" : s.done ? "text-foreground" : "text-muted-foreground"}>{s.name}</span>
                      {s.locked && <button onClick={() => navigate("/app/subscription")} className="text-[10px] text-amber-500/80 ml-auto hover:text-amber-400 hover:underline">Upgrade</button>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
